// ===== Packages =====

import {
    Descendant,
} from 'slate';

/**
 * Internal-link targets can live inside nested Slate structures such as list
 * items or block quotes. Walk the subtree explicitly so progressive reveal can
 * map any descendant target back to the top-level block that must be released.
 */
function nodeContainsParagraphId(
    node: Descendant,
    paragraphId: string,
): boolean {
    const candidateParagraphId = typeof (node as {
        paragraphId?: unknown,
    }).paragraphId === 'string'
        ? `${(node as {
            paragraphId: string,
        }).paragraphId}`.trim()
        : '';
    if (candidateParagraphId === paragraphId) {
        return true;
    }

    const childNodes = Array.isArray((node as {
        children?: Descendant[],
    }).children)
        ? (node as {
            children: Descendant[],
        }).children
        : [];

    return childNodes.some((childNode) => nodeContainsParagraphId(childNode, paragraphId));
}

/**
 * Resolves how many top-level Slate blocks need to be visible for an internal
 * post-link target to exist in the rendered DOM, plus an optional trailing
 * buffer to avoid stopping exactly on the target block.
 */
const resolveInternalPostLinkRevealBlockCount = ({
    slateValue,
    paragraphId,
    bufferBlockCount = 0,
}: {
    slateValue: Descendant[],
    paragraphId: string,
    bufferBlockCount?: number,
}): number | null => {
    const normalizedParagraphId = `${paragraphId || ''}`.trim();
    if (!normalizedParagraphId) {
        return null;
    }

    const targetBlockIndex = slateValue.findIndex((node) => (
        nodeContainsParagraphId(node, normalizedParagraphId)
    ));
    if (targetBlockIndex < 0) {
        return null;
    }

    const normalizedBufferBlockCount = Math.max(0, Math.floor(bufferBlockCount));
    return Math.min(
        slateValue.length,
        targetBlockIndex + 1 + normalizedBufferBlockCount,
    );
};

export default resolveInternalPostLinkRevealBlockCount;
